import React, { useEffect, useState } from 'react';
import { RecurlyProvider, Elements } from '@recurly/react-recurly';
import { useParams } from 'react-router-dom';
import {
    Grid,
    Typography,
    Input,
    FormControl,
    InputLabel,
    Button,
    IconButton,
} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import useSessionstorage from '@rooks/use-sessionstorage';
import { saveAs } from 'file-saver';
import { useScript } from '../../../hooks/useScript';
import config from '../../../auth-config';
import { EditButton, WidgetTemplate, TableTemplate } from './';
import { billingConfig } from './billingConfig';
import { BillingForm } from './BillingForm';
import { isValidEmail } from '../../../helpers/isValidEmail';
import { billing } from '../../../services/billing';
import { base64ToArrayBuffer } from '../../../helpers/base64ToArrayBuffer';
import { BillingCurrency, BillingPlans, countries } from '../../../data';
import ModalPrompt from "../../OrgManage/Components/ModalPrompt";
import { useTranslation } from 'react-i18next';

const { recurlyData } = config;
const { futureChargesTable, invoicesTable } = billingConfig;

const useStyles = makeStyles((theme) => ({
    capitalLetter: {
        textTransform: 'capitalize',
        margin: theme.spacing(1),
        paddingLeft: theme.spacing(1),
    },
    tableTitle: {
        position: 'relative',
        paddingLeft: theme.spacing(2),
        paddingBottom: theme.spacing(1),
        fontSize: '1rem',
        fontWeight: 600,
    },
    table: {
        marginBottom: theme.spacing(3),
        '&:last-child': {
            marginBottom: 0,
        },
    },
    formControl: {
        position: 'relative',
        minWidth: 120,
        width: '80%',
        marginBottom: theme.spacing(3),
        '& .MuiInputLabel-asterisk': {
            display: 'none',
        },
    },
    newEmailBtn: {
        position: 'absolute',
        right: '0',
        top: '50%',
        width: '20%',
        transform: 'translate(105%,-33%)',
    },
    deleteEmailBtn: {
        position: 'absolute',
        right: '0',
        top: '50%',
        transform: 'translate(150%,-55%)',
    },
    emailList: {
        position: 'relative',
        minWidth: 120,
        width: '80%',
        fontSize: '1rem',
        marginBottom: theme.spacing(2),
    },
    preLoader: {
        display: 'block',
        margin: `${theme.spacing(3)}px auto`,
    },
}));

const Billing = ({ orgInfo }) => {
    const { t } = useTranslation();
    const scriptStatus = useScript(recurlyData.url);
    const [isVisibleEditBilling, setVisibleEditBilling] = useState(false);
    const [newEmail, setNewEmail] = useState('');
    const [billingData, setBillingData] = useState({
        billing_info: {
            city: '',
            country: '',
            exp_month: '',
            exp_year: '',
            first_name: '',
            last_name: '',
            first_six: '',
            last_four: '',
            street1: '',
            street2: '',
            region: '',
            phone: '',
            postal_code: '',
        },
        email: '',
        subscriptions: [],
        invoices: [],
        cc_emails: [],
    });
    const [orgBilling, setOrgBilling] = useState({
        billing_curr: BillingCurrency[0],
        billing_plan: BillingPlans[0].plan_code
    });
    const { value: sessionstorageValue } = useSessionstorage('orgInfo', {});
    const classes = useStyles();
    const { orgName } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);

    const handleSetBilling = (input_billing) => {
        var i = 0;
        var l_billingInfo = input_billing;

        for (i = 0; i < l_billingInfo.invoices.length; i++) {
            l_billingInfo.invoices[i].total_display = l_billingInfo.invoices[i].total.toFixed(2).toString() + ' ' + l_billingInfo.invoices[i].currency;
        }
        for (i = 0; i < l_billingInfo.subscriptions.length; i++) {
            l_billingInfo.subscriptions[i].estimated_total_display = l_billingInfo.subscriptions[i].estimated_total.toFixed(2).toString() + " " + l_billingInfo.subscriptions[i].currency;
        }

        setBillingData(l_billingInfo);
    };

    useEffect(() => {
        (async () => {
            try {
                const { status, data } = await billing.getBillingData(
                    sessionstorageValue.orgId
                );

                const { error, billing_account, org_billing } = data;

                if (status === 404) {
                    setVisibleEditBilling(true);
                    return setIsLoading(false);
                }

                if (error !== 'OK' && status !== 200) {
                    alert(t("sorryUnableToRetrieve"));
                    throw new Error(error);
                }

                handleSetBilling(billing_account);
                setOrgBilling(org_billing);
                setIsLoading(false);
            } catch (error) {
                console.error(error);
            }
        })();
    }, []);

    const handleEditClick = () => setVisibleEditBilling(true);

    const handleHideClick = () => setVisibleEditBilling(false);

    const handleSetNewEmail = ({ target }) => setNewEmail(target.value);

    const handleSubmitNewEmail = async (e) => {
        try {
            e.preventDefault();
            if (newEmail && !isValidEmail(newEmail)) {
                return alert(t("youHaveEnteredAnInvalidEmail"));
            }

            const {
                status,
                data,
            } = await billing.updateEmails(sessionstorageValue.orgId, [
                ...billingData.cc_emails,
                newEmail,
            ]);

            const { error, billing_account } = data;

            if (error !== 'OK' && (status !== 200 || status !== 404)) {
                throw new Error(error);
                alert(error);
            }

            handleSetBilling(billing_account);
            setIsLoading(false);
            setNewEmail('');
        } catch (error) {
            console.error(error);
        }
    };

    const handleDeleteEmail = async ({ currentTarget }) => {
        try {
            const email = currentTarget.getAttribute('data-email');

            const { status, data } = await billing.updateEmails(
                sessionstorageValue.orgId,
                billingData.cc_emails.filter((el) => el !== email)
            );

            const { error, billing_account } = data;

            if (error !== 'OK' && (status !== 200 || status !== 404)) {
                throw new Error(error);
            }

            handleSetBilling(billing_account);
            setIsLoading(false);
        } catch (error) {
            console.error(error);
            alert(error);
        }
    };

    const handleDeleteAccount = () => {
        setOpenDeleteConfirm(true);
    };
    const handleCloseDeleteConfirm = () => {
        setOpenDeleteConfirm(false);
    };

    const deleteAccount = async () => {
        try {
            const { status, data } = await billing.deleteAccount(sessionstorageValue.orgId);
            const { error } = data;
            if (error !== 'OK' && (status !== 200 || status !== 404)) {
                throw new Error(error);
            }
            setBillingData({
                billing_info: {
                    city: '',
                    country: '',
                    exp_month: '',
                    exp_year: '',
                    first_name: '',
                    last_name: '',
                    first_six: '',
                    last_four: '',
                    street1: '',
                    street2: '',
                    region: '',
                    phone: '',
                    postal_code: '',
                },
                email: '',
                subscriptions: [],
                invoices: [],
                cc_emails: [],
            });
            alert(t("contactGracoCustomerService"));
        } catch (error) {
            console.error(error);
            alert(error);
        }

    };

    const handleDownloadInvoice = async ({ currentTarget }) => {
        try {
            const invoiceId = currentTarget.getAttribute('data-id');

            const { status, data } = await billing.downloadInvoice(
                sessionstorageValue.orgId,
                invoiceId
            );

            if (status !== 200) {
                throw new Error(t("errorWithDownloading"));
            }

            const pdfFile = new Blob([base64ToArrayBuffer(data)], {
                type: 'application/pdf',
            });

            saveAs(pdfFile, t("invoice") + '_' + invoiceId + '.pdf');
        } catch (error) {
            console.error(error);
            alert(error);
        }
    };

    const {
        subscriptions,
        invoices,
        cc_emails,
        billing_info,
        email,
    } = billingData;

    const {
        city,
        country,
        exp_month,
        exp_year,
        first_name,
        last_name,
        first_six,
        last_four,
        street1,
        street2,
        region,
        phone,
        postal_code,
    } = billing_info;
    const { isOrgEditor } = sessionstorageValue;
    
    function findRegionTranslationKey(country_in, region_in){
        var regionTranlsationKey = undefined;
        const country_item = countries.find(({countryShortCode})=> countryShortCode === country_in );
        var i = 0;
        try{
            for (i=0; i<country_item.regions.length; i++){
                if (country_item.regions[i].name === region_in){
                    regionTranlsationKey = country_item.regions[i].translationKey;
                    break;
                }
            }
        }
        catch (error) {
            //console.error(error);
        }
        return regionTranlsationKey;
    }

    return (
        <>
            {openDeleteConfirm && <ModalPrompt title={t("deleteAccount")}
                content={t("areYouSure")}
                action="Delete"
                handleAction={deleteAccount}
                handleCancel={handleCloseDeleteConfirm}
                openModal={openDeleteConfirm} />}
            <Typography className={classes.capitalLetter} variant="h5">
                {orgName}
            </Typography>
            {isLoading && <CircularProgress className={classes.preLoader} />}
            {!isLoading && (
                <Grid container spacing={2}>
                    <Grid item xs="12" md="8">
                        <TableTemplate
                            title={futureChargesTable.title}
                            subtitle={futureChargesTable.subtitle}
                            columnTitles={futureChargesTable.columnTitles}
                            rows={subscriptions}
                        />
                        <TableTemplate
                            title={invoicesTable.title}
                            columnTitles={invoicesTable.columnTitles}
                            rows={invoices}
                            handleCellClick={handleDownloadInvoice}
                        />
                    </Grid>
                    <Grid item xs="12" md="4">
                        {!isVisibleEditBilling ? (
                            <>
                                <WidgetTemplate
                                    title={
                                        <>
                                            {t("billingInformation")}
                                            {isOrgEditor && (
                                                <EditButton
                                                    clickHandler={
                                                        handleEditClick
                                                    }
                                                    disabled={
                                                        isVisibleEditBilling
                                                    }
                                                />
                                            )}
                                        </>
                                    }>
                                    <p>
                                        {t("creditCard")}: {first_six?.slice(0, 4)}{' '}
                                        {first_six?.slice(4, 6)}.. ....{' '}
                                        {last_four}
                                    </p>
                                    <p>{t("expiration")}: {exp_month + '/' + exp_year}</p>
                                    <p>{t("name") + ": "}{first_name + ' ' + last_name}</p>
                                    <p> {t("preferredCurrency")}: {orgBilling.billing_curr}</p>
                                    <p> {t("subscriptions")}: {t(orgBilling.billing_plan)}</p>
                                </WidgetTemplate>
                                <WidgetTemplate title={t("billingAddress")}>
                                    <p>{t("email") + ": "}{email}</p>
                                    <p>{t("addressLine1") + ": "}{street1}</p>
                                    <p>{t("addressLine2") + ": "}{street2}</p>
                                    <p>{t("city") + ": "}{city}</p>
                                    <p>{t("country") + ": "}{country}</p>
                                    <p>{t("stateProvinceRegion") + ": "}{t(findRegionTranslationKey(country, region))}</p>
                                    <p>{t("zipCode") + ": "}{postal_code}</p>
                                    <p>{t("phone") + ": "}{phone}</p>
                                </WidgetTemplate>
                                <WidgetTemplate title={t('billingCopyList')}>
                                    <form onSubmit={handleSubmitNewEmail}>
                                        {isOrgEditor && (
                                            <FormControl
                                                size="small"
                                                error={
                                                    newEmail &&
                                                    !isValidEmail(newEmail)
                                                }
                                                required
                                                className={classes.formControl}>
                                                <InputLabel id="add-email">
                                                    {t("email")}
                                                </InputLabel>
                                                <Input
                                                    type="email"
                                                    labelId="add-email"
                                                    value={newEmail}
                                                    onChange={handleSetNewEmail}
                                                />
                                                <Button
                                                    type="submit"
                                                    className={
                                                        classes.newEmailBtn
                                                    }>
                                                    {"+ " + t("add")}
                                                </Button>
                                            </FormControl>
                                        )}
                                    </form>
                                    <form onSubmit={(e) => e.preventDefault()}>
                                        {!!cc_emails.length &&
                                            cc_emails.map((email) => (
                                                <FormControl
                                                    key={email.slice(0, 4)}
                                                    size="small"
                                                    className={
                                                        classes.emailList
                                                    }>
                                                    <Typography variant="subtitle1">
                                                        {email}
                                                    </Typography>
                                                    {isOrgEditor && (
                                                        <IconButton
                                                            size="small"
                                                            type="submit"
                                                            color="primary"
                                                            data-email={email}
                                                            className={
                                                                classes.deleteEmailBtn
                                                            }
                                                            onClick={
                                                                handleDeleteEmail
                                                            }>
                                                            <DeleteForeverIcon />
                                                        </IconButton>
                                                    )}
                                                </FormControl>
                                            ))}
                                    </form>
                                </WidgetTemplate>
                                {billingData.email !== "" && billingData.subscriptions.length == 0 && <Button variant="contained" onClick={handleDeleteAccount}>{t("deleteAccount_caps")}</Button>}
                            </>
                        ) : (
                            <>
                                {scriptStatus && (
                                    <>
                                        <Typography
                                            className={classes.tableTitle}
                                            variant="h6">
                                            {t("billingInformation")}
                                        </Typography>
                                        <RecurlyProvider
                                            publicKey={recurlyData.key}>
                                            <Elements>
                                                <BillingForm
                                                    handleHideEditClick={
                                                        handleHideClick
                                                    }
                                                    setBillingData={
                                                        handleSetBilling
                                                    }
                                                    setVisibleEditBilling={
                                                        setVisibleEditBilling
                                                    }
                                                    setOrgBilling={setOrgBilling}
                                                    isData={!!billingData.billing_info.first_six}
                                                    hasSubscription={billingData.subscriptions.length > 0 ? true : false}
                                                    username={sessionstorageValue.orgName + ":" + sessionstorageValue.orgId}
                                                />
                                            </Elements>
                                        </RecurlyProvider>
                                    </>
                                )}

                            </>
                        )}
                    </Grid>
                </Grid>
            )}
        </>
    );
};

export default Billing;